import type { TFunction } from 'i18next';
import type { ProcessDraftStep, PublishValidationError } from '@bliks-io/contracts';
import type { DraftEditorEvent } from '../draftEvents';

export interface PublishErrorStepSummary {
  _id: string;
  generatedName?: string | null;
  task?: { taskDescription?: string | null } | null;
  /**
   * Decision rule `_id` → `output` label, used to name the offending rule on
   * `rule-no-output` graph errors. Callers that don't have rules handy can
   * omit this; the formatter falls back to a generic message.
   */
  rulesById?: Map<string, { output?: string | null }>;
}

export interface PublishErrorEventSummary {
  _id: string;
  name?: string | null;
}

export interface PerStepIssues {
  content: string[];
  graph: string[];
}

/**
 * Publish errors split into per-node entries (keyed by stableStepId for steps
 * or the raw event id for events — both ARE canvas node ids on the unified
 * canvas) and process-level entries (no stepId — e.g. `no-start-event`). The
 * latter can't be attached to any single node, so consumers render them in a
 * dedicated section instead.
 */
export interface PublishIssueBuckets {
  perStep: Map<string, PerStepIssues>;
  processLevel: string[];
}

/**
 * Paths emitted by the backend validator and the stale-conflict diff that we
 * have translated labels for. Anything unlisted falls back to the raw path
 * (no translation noise, no error).
 */
const KNOWN_FIELD_PATHS: Record<string, string> = {
  actionType: 'actionType',
  'task.taskDescription': 'taskDescription',
  'task.output': 'output',
  rules: 'rules',
  assignedTo: 'assignedTo',
  inputs: 'inputs',
  helpers: 'helpers',
  'helpers.persons': 'helperPersons',
  'helpers.orgFunctions': 'helperOrgFunctions',
  'helpers.systems': 'helperSystems',
  'helpers.documents': 'helperDocuments',
  whoElseDoesThis: 'whoElseDoesThis',
  generatedName: 'generatedName',
  generatedDescription: 'generatedDescription',
  manualCapacity: 'manualCapacity'
};

export function getFieldLabel(t: TFunction, fieldPath: string): string {
  const key = KNOWN_FIELD_PATHS[fieldPath];
  return key ? t(`processDraft.publishErrors.fieldLabels.${key}`) : fieldPath;
}

/**
 * The publish validator emits its content-issue messages as English literals
 * (`validateDraftContent` in contracts); map the known ones to translated, self-contained
 * sentences so the publish-error UI stays in the user's language. Unknown
 * messages fall back to the raw text with the field-label prefix.
 */
const KNOWN_ISSUE_MESSAGES: Record<string, string> = {
  'actionType is required for steps': 'actionTypeRequired',
  'Task description is required': 'taskDescriptionRequired',
  'Task output is required': 'taskOutputRequired',
  'At least one decision rule is required': 'rulesRequired',
  'Rule output is required': 'ruleOutputRequired',
  'Rule must have at least one condition value filled in': 'ruleConditionRequired',
  'Steps must have an assignee': 'stepAssigneeRequired',
  'Assignments must have an assignee': 'assignmentAssigneeRequired',
  'Assignments must have at least one previous_output input': 'assignmentInputRequired',
  'Event name is required': 'eventNameRequired',
  'Start event description is required': 'startEventDescriptionRequired'
};

function translateIssueMessage(t: TFunction, message: string): string | null {
  const key = KNOWN_ISSUE_MESSAGES[message];
  return key ? t(`processDraft.publishErrors.issueMessages.${key}`) : null;
}

function pathKey(path: readonly (string | number)[]): string {
  return path.map((p) => String(p)).join('.');
}

export function buildPerStepIssues(
  error: PublishValidationError,
  t: TFunction,
  stepsById?: Map<string, PublishErrorStepSummary>,
  eventsById?: Map<string, PublishErrorEventSummary>
): PublishIssueBuckets {
  const perStep = new Map<string, PerStepIssues>();
  const processLevel: string[] = [];
  for (const c of error.contentErrors ?? []) {
    const entry = perStep.get(c.stepId) ?? { content: [], graph: [] };
    for (const issue of c.issues) {
      // A translated message is a self-contained sentence — no label prefix.
      const translated = translateIssueMessage(t, issue.message);
      if (translated) {
        entry.content.push(translated);
        continue;
      }
      const key = pathKey(issue.path);
      const label = getFieldLabel(t, key);
      // `getFieldLabel` echoes the raw path when there's no translation —
      // skip the "label: message" prefix in that case to avoid noise.
      entry.content.push(label === key ? issue.message : `${label}: ${issue.message}`);
    }
    perStep.set(c.stepId, entry);
  }
  const pushGraphMessage = (nodeId: string, message: string): void => {
    const entry = perStep.get(nodeId) ?? { content: [], graph: [] };
    entry.graph.push(message);
    perStep.set(nodeId, entry);
  };
  for (const g of error.graphErrors ?? []) {
    // `event-disconnected` is always an event. `unreachable` is an event iff
    // eventsById confirms.
    const isEventError =
      !!g.stepId &&
      (g.reason === 'event-disconnected' ||
        (g.reason === 'unreachable' && !!eventsById?.has(g.stepId)));
    if (isEventError && g.stepId) {
      const name = eventsById?.get(g.stepId)?.name;
      pushGraphMessage(
        g.stepId,
        name
          ? t(`processDraft.graphReason.${g.reason}-named`, { name })
          : t(`processDraft.graphReason.${g.reason}`)
      );
      continue;
    }
    const message = formatGraphErrorMessage(g, t, stepsById);
    if (!g.stepId) {
      processLevel.push(message);
      continue;
    }
    pushGraphMessage(g.stepId, message);
  }
  return { perStep, processLevel };
}

type GraphError = NonNullable<PublishValidationError['graphErrors']>[number];

function formatGraphErrorMessage(
  g: GraphError,
  t: TFunction,
  stepsById?: Map<string, PublishErrorStepSummary>
): string {
  if (g.reason === 'rule-no-output') {
    const output =
      g.stepId && g.ruleId ? stepsById?.get(g.stepId)?.rulesById?.get(g.ruleId)?.output : undefined;
    return output
      ? t('processDraft.graphReason.rule-no-output', { output })
      : t('processDraft.graphReason.rule-no-output-unnamed');
  }
  return t(`processDraft.graphReason.${g.reason}`);
}

/**
 * Build the step-summary map the publish-error dialog looks up labels from.
 * Kept here so the page doesn't have to know the shape — just pass the draft
 * steps in and wire the result through.
 */
export function buildStepsById(
  steps: readonly ProcessDraftStep[]
): Map<string, PublishErrorStepSummary> {
  const map = new Map<string, PublishErrorStepSummary>();
  for (const s of steps) {
    const stable = String(s.stableStepId);
    const rulesById = new Map<string, { output?: string | null }>();
    for (const rule of s.rules ?? []) {
      if (!rule._id) continue;
      rulesById.set(String(rule._id), { output: rule.output });
    }
    map.set(stable, {
      _id: stable,
      generatedName: s.generatedName,
      task: s.task ? { taskDescription: s.task.taskDescription } : null,
      rulesById: rulesById.size > 0 ? rulesById : undefined
    });
  }
  return map;
}

// Keyed by the event id, which IS the canvas node id (raw stable id) on the
// unified canvas — so downstream lookups match without translating.
export function buildEventsById(
  events: readonly DraftEditorEvent[]
): Map<string, PublishErrorEventSummary> {
  const map = new Map<string, PublishErrorEventSummary>();
  for (const ev of events) {
    map.set(ev._id, { _id: ev._id, name: ev.name });
  }
  return map;
}

export function getStepLabel(
  nodeId: string,
  stepsById: Map<string, PublishErrorStepSummary>,
  t: TFunction,
  eventsById?: Map<string, PublishErrorEventSummary>
): string {
  const step = stepsById.get(nodeId);
  const stepName = step?.generatedName || step?.task?.taskDescription;
  if (stepName) return stepName;
  const event = eventsById?.get(nodeId);
  if (event) return event.name || t('processDraft.publishErrors.unnamedEvent');
  return t('processDraft.publishErrors.unnamedStep');
}
